'use strict';

/**
 * azure-functions/shared/obligationResponsibility.cjs
 *
 * Who owes an obligation, derived rather than stored.
 *
 * Canonical implementation, re-exported for the frontend by
 * src/utils/obligationResponsibility.js. It runs on the server (the portal
 * decides which questions to show a control owner) and in the browser (the
 * compliance lead's screens report who owes what), and both must reach the same
 * answer — a split between them is the failure COP-B-004 names.
 *
 * THE DERIVATION (COPA-04, founder decision)
 * ------------------------------------------
 * An obligation with no explicit assignee inherits the owner of the control(s)
 * it is mapped to. An explicit assignee always wins. Nothing new is stored:
 * `state.controlOwnership` and `enabledObligations[].assigneeId` both already
 * exist, and this is a reading of them (§12, reuse before invention).
 *
 * An obligation mapped to three controls with three different owners is owed by
 * ALL THREE — each of them sees it — and one accepted answer satisfies it, with
 * the record naming who gave it. "The first owner in the array" is specifically
 * not the rule: which human answers a compliance question must never be decided
 * by array position.
 *
 * UNRESOLVED IS UNASSIGNED, AND `null` IS NEITHER
 * -----------------------------------------------
 * An owner `controlOwnerResolution` cannot resolve to a current, reachable
 * person contributes nothing to `responsibleEmployeeIds` and is reported in
 * `ownerIssues` instead, so the surface that counts coverage and the surface
 * that offers a way to fix it agree (COP-B-004).
 *
 * `responsibleEmployeeIds === null` means "not derived here" — the caller did
 * not supply the tenant's user list — and is deliberately distinct from `[]`,
 * which means "derived, and nobody owes it". A surface that renders null as
 * either owned or unowned is inventing an answer it was not given.
 */

const {
  readOwnerId,
  resolveControlOwner,
  isAssignableOwner,
} = require('./controlOwnerResolution.cjs');

/**
 * @param {object} args
 * @param {object} args.enabledObligation  the tenant's enabledObligations entry
 * @param {string[]} args.controlIds       the controls this obligation is mapped to
 * @param {object} args.state              tenant state (controlOwnership, employees, tenantDomains)
 * @param {Array|null} args.users          tenant users; null/undefined ⇒ not derived
 * @returns {{
 *   responsibleEmployeeIds: string[]|null,
 *   responsible: Array<{employeeId: string, name: string|null, viaControlId: string, explicit: boolean}>,
 *   ownerIssues: Array<{controlId: string|null, status: string, ownerId: string|null}>,
 *   derivedFrom: 'explicit-assignee'|'control-ownership'|null,
 * }}
 */
function deriveResponsibility({ enabledObligation, controlIds, state, users }) {
  if (!Array.isArray(users)) {
    return {
      responsibleEmployeeIds: null,
      responsible: [],
      ownerIssues: [],
      derivedFrom: null,
    };
  }

  const employees = (state && state.employees) || [];
  const tenantDomains = (state && state.tenantDomains) || [];
  const controlOwnership = (state && state.controlOwnership) || {};

  const explicitAssigneeId = (enabledObligation && enabledObligation.assigneeId) || null;

  // An explicit assignment always wins over the derivation. It is still resolved
  // through the same path, because a person named explicitly can also have left.
  if (explicitAssigneeId) {
    const resolution = resolveControlOwner({
      ownerId: explicitAssigneeId,
      users,
      employees,
      tenantDomains,
    });
    if (isAssignableOwner(resolution)) {
      return {
        responsibleEmployeeIds: [resolution.employeeId],
        responsible: [
          {
            employeeId: resolution.employeeId,
            name: resolution.personName,
            viaControlId: null,
            explicit: true,
          },
        ],
        ownerIssues: [],
        derivedFrom: 'explicit-assignee',
      };
    }
    return {
      responsibleEmployeeIds: [],
      responsible: [],
      ownerIssues: [
        { controlId: null, status: resolution.status, ownerId: explicitAssigneeId },
      ],
      derivedFrom: 'explicit-assignee',
    };
  }

  const responsible = [];
  const ownerIssues = [];
  const seen = new Set();

  for (const controlId of controlIds || []) {
    const ownerId = readOwnerId(controlOwnership, controlId);
    if (!ownerId) continue; // an unowned control is a gap ownershipGaps reports, not an issue here
    const resolution = resolveControlOwner({ ownerId, users, employees, tenantDomains });
    if (!isAssignableOwner(resolution)) {
      ownerIssues.push({ controlId, status: resolution.status, ownerId });
      continue;
    }
    if (seen.has(resolution.employeeId)) continue;
    seen.add(resolution.employeeId);
    responsible.push({
      employeeId: resolution.employeeId,
      name: resolution.personName,
      viaControlId: controlId,
      explicit: false,
    });
  }

  return {
    responsibleEmployeeIds: responsible.map((r) => r.employeeId),
    responsible,
    ownerIssues,
    derivedFrom: 'control-ownership',
  };
}

/** Does this person owe this obligation? `null` (not derived) is never a yes. */
function isResponsibleFor(responsibility, employeeId) {
  const ids = responsibility && responsibility.responsibleEmployeeIds;
  return Array.isArray(ids) && ids.includes(employeeId);
}

module.exports = { deriveResponsibility, isResponsibleFor };
